import { AuthTabs } from "../../login/AuthTabs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HeaderLG } from "../../design-system/typography";
import { Link } from "react-router-dom";
import { SignupStepProps } from "../signupForm";
import { cn } from "@verse_core/shared_components/ClassName";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";

type RoleOptionProps = {
  title: string;
  description: string;
} & (
  | { to: string; onClick?: never; disabled?: never }
  | { to?: never; onClick: () => void; disabled?: never }
  | { to?: never; onClick?: never; disabled: true }
);

/** A role row: a link when given `to`, otherwise a button. */
const RoleOption = ({
  title,
  description,
  to,
  onClick,
  disabled,
}: RoleOptionProps) => {
  const className = cn(
    "tw-flex tw-w-full tw-items-center tw-gap-[16px] tw-rounded-[8px] tw-border tw-border-solid tw-border-verse-border-primary tw-bg-white tw-p-[16px] tw-text-left tw-font-sohne hover:tw-no-underline",
    disabled && "tw-cursor-not-allowed tw-opacity-50",
  );
  const content = (
    <>
      <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[4px] tw-leading-[1.4]">
        <span className="tw-text-[14px] tw-text-verse-text-primary">
          {title}
        </span>
        <span className="tw-text-[12px] tw-text-verse-text-tertiary">
          {description}
        </span>
      </span>
      <FontAwesomeIcon
        icon={faChevronRight}
        className="tw-h-[16px] tw-w-[16px] tw-shrink-0 tw-text-verse-icon-primary"
      />
    </>
  );

  return to ? (
    <Link to={to} reloadDocument className={className}>
      {content}
    </Link>
  ) : (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={className}
    >
      {content}
    </button>
  );
};

/** The first sign-up screen: pick a role. Clinicians continue into the
 * sign-up flow and patients go to the patient portal. */
export const RoleStep = ({ onNext }: SignupStepProps) => (
  <div className="tw-flex tw-w-full tw-flex-col tw-gap-[40px]">
    <AuthTabs selected="signup" />
    <div className="tw-flex tw-flex-col tw-gap-[24px]">
      <HeaderLG>Select your role to continue:</HeaderLG>
      <div className="tw-flex tw-flex-col tw-gap-[10px]">
        <RoleOption
          title="Clinician"
          description="Sign up to place DME orders"
          onClick={onNext}
        />
        <RoleOption
          title="Patient"
          description="Track your DME order online"
          to="/patient-portal"
        />
        {/* Greyed out until a subcontractor sign-up exists. */}
        <RoleOption
          title="Subcontractor"
          description="Get started to coordinate on DME orders"
          disabled
        />
      </div>
    </div>
  </div>
);
